import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { removeCookie } from '../utils/cookieUtils';
import AuthService from '../services/auth.service';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    async function checkAuthStatus() {
      setLoading(true);
      try {
        if (process.env.NODE_ENV === 'development') {
          setUser({
            id: 'dev-user-001',
            email: 'dev@example.com',
            fullName: 'Dev User',
            isPatron: true, 
            is_forced: true,
          });
          setLoading(false);
          return; 
        }

        const userData = await AuthService.getCurrentUser();
        
        // Check if the user data returned successfully
        if (!userData.error) {
          setUser(userData);
        } else {
          // User is not authenticated
          setUser(null);
        }
      } catch (err) {
        console.error('Auth status check failed:', err);
        setError('Unable to check authentication status');
        setUser(null);
      } finally {
        setLoading(false);
      }
    }

    checkAuthStatus();
  }, []);

  const login = () => {
    // Redirect to the Patreon OAuth login endpoint on the backend
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/patreon`;
  };

  const logout = async () => {
    try {
      // Call the logout service
      await AuthService.logout();
      
      // Clear auth data from memory
      setUser(null);
      
      // Clear cookies by expiring them
      removeCookie('auth-token');
      
      // Navigate to landing page
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Logout failed');
    }
  };

  // Check if user is a patron or has forced access
  const isPatron = () => {
    // Allow access if the user has is_forced set to true OR is a patron
    return (user && (user.is_forced === true || user.isPatron === true));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        error,
        login,
        logout,
        isPatron,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext; 